import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Person,
  Email,
  Business,
  Lock,
  Edit,
  Save,
  Cancel,
  Refresh,
  CameraAlt,
  Shield,
  Key,
  CloudUpload,
  CheckCircle,
  Badge,
  School,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import { userService } from '../services/userService';
import { User } from '../types';

/* ── validation schemas (stable, never re-created) ── */
const profileUpdateSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email'),
});

const passwordChangeSchema = yup.object({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup.string().required('New password is required'),
  confirmPassword: yup.string()
    .required('Please confirm your password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

const adminPasswordResetSchema = yup.object({
  newPassword: yup.string().required('New password is required'),
  confirmPassword: yup.string()
    .required('Please confirm your password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

const buildLogoUrl = (filename: string | undefined): string | null => {
  if (!filename) return null;
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const origin = apiUrl.replace(/\/api\/?$/, '');
  return `${origin}/uploads/logos/${filename}`;
};

/* ═══════════════════════════════════════════════════════
   ProfilePage
   ═══════════════════════════════════════════════════════ */
const ProfilePage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user, updateUser, decodedToken } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [editingProfile, setEditingProfile] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [adminResetDialogOpen, setAdminResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoFilename, setLogoFilename] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState(0);

  const isSchoolAdmin = decodedToken?.isSchoolAdmin === true;
  const canResetOthersPasswords = user?.isSystemAdmin || false;

  useEffect(() => {
    if (user?.schoolId?.logo) setLogoFilename(user.schoolId.logo);
  }, [user?.schoolId?.logo]);

  /* ── derived values (memoised) ── */
  const logoUrl = useMemo(() => buildLogoUrl(logoFilename || undefined), [logoFilename]);
  const roleName = useMemo(
    () => decodedToken?.roleName || (user?.isSystemAdmin ? 'Super Admin' : (user?.roleId?.name || 'User')),
    [decodedToken?.roleName, user?.isSystemAdmin, user?.roleId?.name],
  );

  const tabs = useMemo(() => {
    const t = [{ label: 'Personal', icon: <Person sx={{ fontSize: 17 }} /> }];
    if (isSchoolAdmin) t.push({ label: 'School', icon: <School sx={{ fontSize: 17 }} /> });
    return t;
  }, [isSchoolAdmin]);

  /* ── forms ── */
  const profileFormik = useFormik({
    initialValues: { name: user?.name || '', email: user?.email || '' },
    validationSchema: profileUpdateSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const updateData: { name: string; email?: string } = { name: values.name };
        if (!isSchoolAdmin) updateData.email = values.email;
        const updatedUser = await authService.updateProfile(updateData);
        if (updatedUser) updateUser(updatedUser);
        setEditingProfile(false);
        enqueueSnackbar('Profile updated successfully', { variant: 'success' });
      } catch (error: any) {
        enqueueSnackbar(error.message || 'Failed to update profile', { variant: 'error' });
      }
    },
  });

  const passwordFormik = useFormik({
    initialValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    validationSchema: passwordChangeSchema,
    onSubmit: async (values) => {
      try {
        await authService.changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });
        setPasswordDialogOpen(false);
        passwordFormik.resetForm();
        enqueueSnackbar('Password changed successfully', { variant: 'success' });
      } catch (error: any) {
        enqueueSnackbar(error.message || 'Failed to change password', { variant: 'error' });
      }
    },
  });

  const adminResetFormik = useFormik({
    initialValues: { newPassword: '', confirmPassword: '' },
    validationSchema: adminPasswordResetSchema,
    onSubmit: async (values) => {
      if (!selectedUser) return;
      try {
        await userService.resetUserPassword(selectedUser._id, values.newPassword);
        setAdminResetDialogOpen(false);
        setSelectedUser(null);
        adminResetFormik.resetForm();
        enqueueSnackbar(`Password reset for ${selectedUser.name}`, { variant: 'success' });
      } catch (error: any) {
        enqueueSnackbar(error.message || 'Failed to reset password', { variant: 'error' });
      }
    },
  });

  /* ── data loading ── */
  const loadUsers = useCallback(async () => {
    if (!canResetOthersPasswords) return;
    setLoadingUsers(true);
    try {
      const data = await userService.getUsers();
      setUsers(data?.users || []);
    } catch {
      enqueueSnackbar('Failed to load users', { variant: 'error' });
    } finally {
      setLoadingUsers(false);
    }
  }, [canResetOthersPasswords, enqueueSnackbar]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  /* ── handlers (stable refs) ── */
  const handleCancelEdit = useCallback(() => {
    profileFormik.resetForm();
    setEditingProfile(false);
  }, [profileFormik]);

  const handleLogoUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar('Logo must be less than 5 MB', { variant: 'error' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Only image files are allowed', { variant: 'error' });
      return;
    }
    setUploadingLogo(true);
    try {
      const result = await authService.uploadSchoolLogo(file);
      if (result?.logo) {
        setLogoFilename(result.logo);
        const updatedProfile = await authService.getProfile();
        if (updatedProfile) updateUser(updatedProfile);
        enqueueSnackbar('Logo uploaded successfully', { variant: 'success' });
      }
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to upload logo', { variant: 'error' });
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  }, [enqueueSnackbar, updateUser]);

  const handleTabChange = useCallback((idx: number) => setActiveTab(idx), []);

  /* ── shared styles (memoised) ── */
  const cardSx = useMemo(() => ({
    p: 0,
    overflow: 'hidden',
    border: `1px solid ${isDark ? alpha(theme.palette.text.primary, 0.08) : '#e8ecf0'}`,
    borderRadius: 3,
    bgcolor: isDark ? alpha(theme.palette.text.primary, 0.02) : '#fff',
    transition: 'box-shadow 0.25s ease',
    '&:hover': {
      boxShadow: isDark
        ? `0 4px 24px ${alpha('#000', 0.4)}`
        : `0 4px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
    },
  }), [isDark, theme.palette.primary.main]);

  const sectionHeaderSx = useMemo(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    p: 2.5,
    pb: 0,
  }), []);

  const iconBoxSx = useCallback((color: string) => ({
    width: 36, height: 36, borderRadius: 2,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    bgcolor: alpha(color, isDark ? 0.15 : 0.08),
    color,
  }), [isDark]);

  /* ───────────────────── RENDER ───────────────────── */
  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>

      {/* ═══  TAB BAR  ═══ */}
      {isSchoolAdmin && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 0.5,
            display: 'inline-flex',
            gap: 0.5,
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.text.primary, isDark ? 0.04 : 0.04),
            border: `1px solid ${isDark ? alpha(theme.palette.text.primary, 0.06) : 'transparent'}`,
          }}
        >
          {tabs.map((tab, idx) => {
            const isActive = activeTab === idx;
            return (
              <Box
                key={tab.label}
                onClick={() => handleTabChange(idx)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.84rem',
                  userSelect: 'none',
                  transition: 'all 0.22s cubic-bezier(.4,0,.2,1)',
                  color: isActive ? (isDark ? theme.palette.text.primary : theme.palette.primary.main) : 'text.secondary',
                  bgcolor: isActive
                    ? (isDark ? alpha(theme.palette.primary.main, 0.18) : '#fff')
                    : 'transparent',
                  boxShadow: isActive
                    ? (isDark
                        ? `0 1px 6px ${alpha('#000', 0.35)}`
                        : `0 1px 4px ${alpha('#000', 0.08)}`)
                    : 'none',
                  '&:hover': {
                    bgcolor: isActive
                      ? undefined
                      : (alpha(theme.palette.text.primary, isDark ? 0.06 : 0.04)),
                  },
                }}
              >
                {tab.icon}
                {tab.label}
              </Box>
            );
          })}
        </Paper>
      )}

      {/* ═══  TAB PANELS — both always mounted, toggled via display  ═══ */}

      {/* ─── PERSONAL TAB ─── */}
      <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
        <Grid container spacing={2.5}>
          {/* Left: Profile card */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper elevation={0} sx={cardSx}>
              {/* Mini profile header inside the card */}
              <Box
                sx={{
                  p: 2.5,
                  pb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  borderBottom: `1px solid ${isDark ? alpha(theme.palette.text.primary, 0.06) : '#f0f2f5'}`,
                }}
              >
                <Avatar
                  sx={{
                    width: 52, height: 52,
                    fontSize: 22, fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1),
                    color: 'primary.main',
                  }}
                >
                  {user?.name?.charAt(0)?.toUpperCase() || <Person />}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.15 }}>
                    {user?.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Shield sx={{ fontSize: 13 }} />
                    {roleName}
                    {user?.schoolId?.name && (
                      <>
                        <Box component="span" sx={{ mx: 0.5, opacity: 0.4 }}>•</Box>
                        {user.schoolId.name}
                      </>
                    )}
                  </Typography>
                </Box>
                {!editingProfile ? (
                  <Tooltip title="Edit profile" arrow>
                    <IconButton
                      onClick={() => setEditingProfile(true)}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.text.primary, isDark ? 0.05 : 0.04),
                        '&:hover': { bgcolor: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.08) },
                      }}
                    >
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Cancel" arrow>
                      <IconButton onClick={handleCancelEdit} size="small">
                        <Cancel sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Save" arrow>
                      <IconButton
                        onClick={() => profileFormik.handleSubmit()}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) },
                        }}
                      >
                        <Save sx={{ fontSize: 16, color: 'success.main' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              {/* Form fields */}
              <Box sx={{ p: 2.5 }}>
                <form onSubmit={profileFormik.handleSubmit}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth size="small"
                      name="name" label="Full Name"
                      value={profileFormik.values.name}
                      onChange={profileFormik.handleChange}
                      error={profileFormik.touched.name && Boolean(profileFormik.errors.name)}
                      helperText={profileFormik.touched.name && profileFormik.errors.name}
                      disabled={!editingProfile}
                      slotProps={{
                        input: { startAdornment: <Person sx={{ mr: 1, color: 'text.disabled', fontSize: 18 }} /> },
                      }}
                    />

                    {user?.email ? (
                      <TextField
                        fullWidth size="small"
                        name="email" label="Email Address" type="email"
                        value={profileFormik.values.email}
                        onChange={profileFormik.handleChange}
                        error={profileFormik.touched.email && Boolean(profileFormik.errors.email)}
                        helperText={
                          isSchoolAdmin
                            ? 'Email cannot be modified for school administrators'
                            : (profileFormik.touched.email && profileFormik.errors.email)
                        }
                        disabled={!editingProfile || isSchoolAdmin}
                        slotProps={{
                          input: {
                            startAdornment: <Email sx={{ mr: 1, color: 'text.disabled', fontSize: 18 }} />,
                            endAdornment: isSchoolAdmin ? (
                              <Tooltip title="Locked for school admins" arrow>
                                <Lock sx={{ color: 'text.disabled', fontSize: 16 }} />
                              </Tooltip>
                            ) : undefined,
                          },
                        }}
                      />
                    ) : (
                      <TextField
                        fullWidth size="small"
                        label="User ID"
                        value={(user as any)?.userCode || '—'}
                        disabled
                        helperText="Your unique login identifier"
                        slotProps={{
                          input: {
                            startAdornment: <Badge sx={{ mr: 1, color: 'text.disabled', fontSize: 18 }} />,
                            style: { fontFamily: 'monospace', fontWeight: 600 },
                          },
                        }}
                      />
                    )}
                  </Box>
                </form>
              </Box>
            </Paper>
          </Grid>

          {/* Right column */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Security */}
              <Paper elevation={0} sx={cardSx}>
                <Box sx={sectionHeaderSx}>
                  <Box sx={iconBoxSx(theme.palette.warning.main)}>
                    <Shield sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Security</Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Button
                    fullWidth variant="outlined" startIcon={<Key />}
                    onClick={() => setPasswordDialogOpen(true)}
                    sx={{
                      py: 1.2, borderRadius: 2, textTransform: 'none', fontWeight: 500,
                      borderColor: alpha(theme.palette.text.primary, isDark ? 0.12 : 0.12),
                      color: 'text.primary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    Change My Password
                  </Button>
                </Box>
              </Paper>

              {/* Admin Password Reset */}
              {canResetOthersPasswords && (
                <Paper elevation={0} sx={cardSx}>
                  <Box sx={{ ...sectionHeaderSx, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={iconBoxSx(theme.palette.error.main)}>
                        <Key sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Reset User Password
                      </Typography>
                    </Box>
                    <Tooltip title="Refresh users" arrow>
                      <IconButton
                        onClick={loadUsers} size="small" disabled={loadingUsers}
                        sx={{
                          bgcolor: alpha(theme.palette.text.primary, isDark ? 0.05 : 0.04),
                          '&:hover': { bgcolor: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.08) },
                        }}
                      >
                        <Refresh sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                      <InputLabel>Select User</InputLabel>
                      <Select
                        value=""
                        onChange={(e) => {
                          const target = users.find(u => u._id === e.target.value);
                          if (target) { setSelectedUser(target); setAdminResetDialogOpen(true); }
                        }}
                        label="Select User"
                        disabled={loadingUsers || users.length === 0}
                        sx={{ borderRadius: 2 }}
                      >
                        {loadingUsers ? (
                          <MenuItem disabled>Loading…</MenuItem>
                        ) : users.length === 0 ? (
                          <MenuItem disabled>No users</MenuItem>
                        ) : (
                          users.filter(u => u._id !== user?._id).map(u => (
                            <MenuItem key={u._id} value={u._id}>
                              {u.name} ({u.email || (u as any)?.userCode})
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    <Alert
                      severity="info" variant="outlined"
                      sx={{ borderRadius: 2, fontSize: '0.75rem', py: 0, '& .MuiAlert-icon': { fontSize: 16 } }}
                    >
                      Users who forget their password can contact you for a reset.
                    </Alert>
                  </Box>
                </Paper>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ─── SCHOOL TAB (school admins only, always mounted when admin) ─── */}
      {isSchoolAdmin && (
        <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
          <Grid container spacing={2.5}>
            {/* School Details */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper elevation={0} sx={cardSx}>
                <Box sx={sectionHeaderSx}>
                  <Box sx={iconBoxSx(theme.palette.primary.main)}>
                    <Business sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    School Details
                  </Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth size="small"
                      label="School Name" disabled
                      value={user?.schoolId?.name || '—'}
                      slotProps={{
                        input: { startAdornment: <Business sx={{ mr: 1, color: 'text.disabled', fontSize: 18 }} /> },
                      }}
                    />
                    {user?.schoolId?.code && (
                      <TextField
                        fullWidth size="small"
                        label="School Code" disabled
                        value={user.schoolId.code}
                        slotProps={{
                          input: {
                            startAdornment: <Badge sx={{ mr: 1, color: 'text.disabled', fontSize: 18 }} />,
                            style: { fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.05em' },
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* School Logo */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper elevation={0} sx={cardSx}>
                <Box sx={sectionHeaderSx}>
                  <Box sx={iconBoxSx(theme.palette.info.main)}>
                    <ImageIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    School Logo
                  </Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={handleLogoUpload} />

                  {logoUrl ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        p: 3,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.text.primary, isDark ? 0.03 : 0.015),
                        border: `1px solid ${isDark ? alpha(theme.palette.text.primary, 0.06) : '#f0f0f0'}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 110, height: 110,
                          borderRadius: 3,
                          overflow: 'hidden',
                          border: `2px solid ${isDark ? alpha(theme.palette.text.primary, 0.08) : '#eee'}`,
                          bgcolor: isDark ? alpha(theme.palette.text.primary, 0.04) : '#fff',
                          display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          boxShadow: isDark
                            ? `0 4px 16px ${alpha('#000', 0.3)}`
                            : `0 4px 16px ${alpha('#000', 0.06)}`,
                        }}
                      >
                        <Box
                          component="img"
                          src={logoUrl}
                          alt="School Logo"
                          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', p: 1 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <CheckCircle sx={{ fontSize: 15, color: 'success.main' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main', fontSize: '0.8rem' }}>
                          Logo uploaded
                        </Typography>
                      </Box>
                      <Button
                        size="small" variant="outlined"
                        startIcon={uploadingLogo ? <CircularProgress size={14} /> : <CameraAlt sx={{ fontSize: 15 }} />}
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        sx={{
                          textTransform: 'none', fontWeight: 500, borderRadius: 2,
                          borderColor: alpha(theme.palette.text.primary, isDark ? 0.15 : 0.12),
                          color: 'text.secondary',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            color: 'primary.main',
                          },
                        }}
                      >
                        {uploadingLogo ? 'Uploading…' : 'Change Logo'}
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      onClick={() => !uploadingLogo && logoInputRef.current?.click()}
                      sx={{
                        border: `2px dashed ${isDark ? alpha(theme.palette.text.primary, 0.12) : '#d4d4d4'}`,
                        borderRadius: 3,
                        py: 4, px: 2,
                        textAlign: 'center',
                        cursor: uploadingLogo ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': uploadingLogo ? {} : {
                          borderColor: 'primary.main',
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                        },
                      }}
                    >
                      {uploadingLogo ? (
                        <CircularProgress size={28} sx={{ mb: 1 }} />
                      ) : (
                        <CloudUpload
                          sx={{
                            fontSize: 36,
                            color: alpha(theme.palette.text.primary, isDark ? 0.25 : 0.2),
                            mb: 0.5,
                          }}
                        />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.3 }}>
                        {uploadingLogo ? 'Uploading…' : 'Click to upload logo'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        PNG, JPG or SVG • Max 5 MB
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ═══  CHANGE PASSWORD DIALOG  ═══ */}
      <Dialog
        open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={passwordFormik.handleSubmit}>
          <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Key sx={{ color: 'primary.main', fontSize: 22 }} />
            Change Password
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth size="small" name="currentPassword"
                label="Current Password" type="password"
                value={passwordFormik.values.currentPassword}
                onChange={passwordFormik.handleChange}
                error={passwordFormik.touched.currentPassword && Boolean(passwordFormik.errors.currentPassword)}
                helperText={passwordFormik.touched.currentPassword && passwordFormik.errors.currentPassword}
              />
              <TextField
                fullWidth size="small" name="newPassword"
                label="New Password" type="password"
                value={passwordFormik.values.newPassword}
                onChange={passwordFormik.handleChange}
                error={passwordFormik.touched.newPassword && Boolean(passwordFormik.errors.newPassword)}
                helperText={passwordFormik.touched.newPassword && passwordFormik.errors.newPassword}
              />
              <TextField
                fullWidth size="small" name="confirmPassword"
                label="Confirm New Password" type="password"
                value={passwordFormik.values.confirmPassword}
                onChange={passwordFormik.handleChange}
                error={passwordFormik.touched.confirmPassword && Boolean(passwordFormik.errors.confirmPassword)}
                helperText={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setPasswordDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={{ textTransform: 'none', borderRadius: 2, boxShadow: 'none' }}>
              Change Password
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ═══  ADMIN RESET DIALOG  ═══ */}
      <Dialog
        open={adminResetDialogOpen} onClose={() => setAdminResetDialogOpen(false)}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={adminResetFormik.handleSubmit}>
          <DialogTitle sx={{ fontWeight: 600 }}>
            Reset Password — {selectedUser?.name}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Resetting password for <strong>{selectedUser?.email || (selectedUser as any)?.userCode}</strong>
              </Alert>
              <TextField
                fullWidth size="small" name="newPassword"
                label="New Password" type="password"
                value={adminResetFormik.values.newPassword}
                onChange={adminResetFormik.handleChange}
                error={adminResetFormik.touched.newPassword && Boolean(adminResetFormik.errors.newPassword)}
                helperText={adminResetFormik.touched.newPassword && adminResetFormik.errors.newPassword}
              />
              <TextField
                fullWidth size="small" name="confirmPassword"
                label="Confirm New Password" type="password"
                value={adminResetFormik.values.confirmPassword}
                onChange={adminResetFormik.handleChange}
                error={adminResetFormik.touched.confirmPassword && Boolean(adminResetFormik.errors.confirmPassword)}
                helperText={adminResetFormik.touched.confirmPassword && adminResetFormik.errors.confirmPassword}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setAdminResetDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="warning" sx={{ textTransform: 'none', borderRadius: 2, boxShadow: 'none' }}>
              Reset Password
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;